import { create } from "zustand";
import axiosInstance from "../common/axiosInstance";
import { handleApiError } from "../common/utils";
import { error } from "console";

export type ResponseType =
  | "yesno"
  | "passfail"
  | "numeric"
  | "rating"
  | "text"
  | "photo";

export interface ChecklistItem {
  id?: string;
  checklistCode?: string;
  category: string;
  questionText: string;
  responseType: ResponseType;
  expectedValue?: number | { min: number; max: number } | string;
  weightage: number;
  isCritical: boolean;
  conditionalQuestion?: {
    dependsOnItemId: string;
    condition: string; // e.g., "yes", "no", "pass", "fail"
    showIf: boolean;
  };
  photoRequired?: boolean;
}

export interface ChecklistTemplate {
  _id?: string;
  checklistCode: string;
  name: string;
  category: string;
  items: ChecklistItem[];
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  tenantId?: string;
}

export interface ComplianceScore {
  complianceScore: number;
  totalWeight: number;
  passedWeight: number;
  totalItems: number;
  passedItems: number;
}

interface SafetyFacilityRoundsState {
  // State
  checklists: ChecklistTemplate[];
  currentChecklist: ChecklistTemplate | null;
  loading: boolean;
  error: string | null;
  categories: string[];

  fetchChecklists: (filters?: {
    category?: string;
    isActive?: boolean;
  }) => Promise<void>;
  fetchChecklistById: (id: string) => Promise<void>;
  createChecklist: (
    data: Omit<
      ChecklistTemplate,
      "_id" | "checklistCode" | "createdAt" | "updatedAt" | "tenantId"
    >
  ) => Promise<ChecklistTemplate>;
  updateChecklist: (
    id: string,
    data: Partial<
      Omit<
        ChecklistTemplate,
        "_id" | "checklistCode" | "createdAt" | "updatedAt" | "tenantId"
      >
    >
  ) => Promise<ChecklistTemplate>;
  deleteChecklist: (id: string) => Promise<void>;
  calculateComplianceScore: (
    templateId: string,
    responses: Array<{
      itemId: string;
      value: any;
      photos?: string[];
    }>
  ) => Promise<ComplianceScore>;
  submitRoundExecution: (data: {
    checklistId: string;
    responses: Array<{ itemId: string; value: any; photos?: string[] }>;
    startedAt?: string;
    completedAt?: string;
  }) => Promise<any>;
  setCurrentChecklist: (checklist: ChecklistTemplate | null) => void;
  clearError: () => void;
}

const CATEGORIES = [
  "Fire Safety",
  "Infection Control",
  "Equipment",
  "Accessibility",
  "Environmental",
  "Structural",
  "Electrical",
  "Plumbing",
  "HVAC",
  "Other",
];

export const useSafetyFacilityRoundsStore = create<SafetyFacilityRoundsState>(
  (set, get) => ({
    checklists: [],
    currentChecklist: null,
    loading: false,
    error: null,
    categories: CATEGORIES,

    // Fetch all checklists
    fetchChecklists: async (filters = {}) => {
      set({ loading: true, error: null });
      try {
        const params = new URLSearchParams();
        if (filters.category) params.append("category", filters.category);
        if (filters.isActive !== undefined)
          params.append("isActive", filters.isActive.toString());

        const response = await axiosInstance.get(
          `/api/safety-facility-rounds-checklist${
            params.toString() ? `?${params.toString()}` : ""
          }`
        );

        if (response.data.success) {
          set({
            checklists: response.data.data || [],
            loading: false,
            error: null,
          });
        } else {
          throw new Error(response.data.error || "Failed to fetch checklists");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch checklists";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
      }
    },

    // Fetch checklist by ID
    fetchChecklistById: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.get(
          `/api/safety-facility-rounds-checklist/${id}`
        );

        if (response.data.success) {
          set({
            currentChecklist: response.data.data,
            loading: false,
            error: null,
          });
        } else {
          throw new Error(response.data.error || "Failed to fetch checklist");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch checklist";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
      }
    },

    createChecklist: async (data) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.post(
          `/api/safety-facility-rounds-checklist`,
          data
        );

        if (response.data.success) {
          const newChecklist = response.data.data;
          set((state) => ({
            checklists: [newChecklist, ...state.checklists],
            currentChecklist: newChecklist,
            loading: false,
            error: null,
          }));
          return newChecklist;
        } else {
          throw new Error(response.data.error || "Failed to create checklist");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to create checklist";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },

    updateChecklist: async (id: string, data) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.put(
          `/api/safety-facility-rounds-checklist/${id}`,
          data
        );

        if (response.data.success) {
          const updatedChecklist = response.data.data;
          set((state) => ({
            checklists: state.checklists.map((c) =>
              c._id === id ? updatedChecklist : c
            ),
            currentChecklist:
              state.currentChecklist?._id === id
                ? updatedChecklist
                : state.currentChecklist,
            loading: false,
            error: null,
          }));
          return updatedChecklist;
        } else {
          throw new Error(response.data.error || "Failed to update checklist");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to update checklist";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },

    deleteChecklist: async (id: string) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.delete(
          `/api/safety-facility-rounds-checklist/${id}`
        );

        if (response.data.success) {
          set((state) => ({
            checklists: state.checklists.filter((c) => c._id !== id),
            currentChecklist:
              state.currentChecklist?._id === id
                ? null
                : state.currentChecklist,
            loading: false,
            error: null,
          }));
        } else {
          throw new Error(response.data.error || "Failed to delete checklist");
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to delete checklist";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },

    submitRoundExecution: async (data) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.post(
          `/api/safety-facility-rounds-checklist/execute`,
          data
        );

        if (response.data.success) {
          set({ loading: false, error: null });
          return response.data.data;
        } else {
          throw new Error(
            response.data.error || "Failed to submit round execution"
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to submit round execution";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },

    calculateComplianceScore: async (templateId: string, responses) => {
      set({ loading: true, error: null });
      try {
        const response = await axiosInstance.post(
          `/api/safety-facility-rounds-checklist/calculate-score`,
          {
            templateId,
            responses,
          }
        );

        if (response.data.success) {
          set({ loading: false, error: null });
          return response.data.data;
        } else {
          throw new Error(
            response.data.error || "Failed to calculate compliance score"
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to calculate compliance score";
        handleApiError(error, errorMessage);
        set({ loading: false, error: errorMessage });
        throw error;
      }
    },

    setCurrentChecklist: (checklist) => {
      set({ currentChecklist: checklist });
    },

    clearError: () => {
      set({ error: null });
    },
  })
);
